/**
 * Detect whether a decrypted bridge fields-schema carries the durable
 * question-model.
 *
 * The producer (med_form_app/backend/routers/bridge.py `_encrypt_fields_schema`)
 * attaches `audience` to every patient-fill entry, plus `conditionalOn` and an
 * explicit boolean `required` wherever the form's `conditional_logic` covers a
 * field. A schema sealed BEFORE that pipeline existed — a "stale" dispatch
 * already sitting in a patient's inbox, or a legacy v1 string-only schema — has
 * none of those keys, so the consumer cannot know which fields are genuinely
 * required.
 *
 * When this returns false, BridgeFieldsForm treats the panel as
 * review-and-sign: no field hard-blocks the signature. That way a patient
 * holding a pre-model assignment can still sign, instead of being trapped
 * behind every empty field — which the old `required !== false` default forced
 * to look mandatory (the "I completed the form but it won't let me sign" bug).
 * Modeled schemas are unaffected and keep their explicit `required` flags.
 */
export function schemaIsModeled(schemaFields: unknown): boolean {
  if (!Array.isArray(schemaFields)) return false;
  // Require evidence of the CONDITIONAL + REQUIRED model — not `audience` alone.
  // The producer tags `audience` on every patient-fill entry, so an audience-
  // only schema (sealed by an intermediate build, or before conditionalOn /
  // required were emitted) would otherwise read as "modeled" and then force
  // every field mandatory via the panel's required default — trapping the
  // patient ("can't sign"). Gating on conditionalOn / boolean required means a
  // partial schema correctly falls through to review-and-sign instead.
  return schemaFields.some(
    (f: any) =>
      f &&
      typeof f === "object" &&
      (f.conditionalOn != null || typeof f.required === "boolean")
  );
}

export default schemaIsModeled;
